import React, { useState, useEffect, useRef } from 'react';

function SeatingPlan({ focusTableId, onFocusHandled }) {
  const [tables, setTables] = useState([]);
  const [guests, setGuests] = useState([]);
  const [highlightedTable, setHighlightedTable] = useState(null);
  const [rsvpMap, setRsvpMap] = useState({}); // guestName -> { kids }
  const [rsvpList, setRsvpList] = useState([]);
  const [totalKids, setTotalKids] = useState(0);
  const [showLabels, setShowLabels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSearch, setActiveSearch] = useState(null); // { tableId, seatIndex }
  const [searchTerm, setSearchTerm] = useState('');
  const [dragSource, setDragSource] = useState(null); // { tableId, seatIndex } or { guest }
  const [dragTable, setDragTable] = useState(null); // tableId being dragged for reorder
  const [dragOverTable, setDragOverTable] = useState(null); // tableId being hovered over
  const searchInputRef = useRef(null);

  // Load RSVPs and seating data
  useEffect(() => {
    Promise.all([
      fetch('/api/admin/rsvps').then(r => r.json()),
      fetch('/api/admin/seating').then(r => r.json()),
    ]).then(([rsvpData, seatingData]) => {
      // Parse individual guest names from RSVPs
      const allGuests = [];
      const rsvpLookup = {};
      let kidsTotal = 0;
      rsvpData.forEach(rsvp => {
        if (rsvp.attending !== 'yes') return;
        const fullName = `${rsvp.firstName} ${rsvp.lastName}`;
        allGuests.push(fullName);
        const guestNamesList = rsvp.guestNames
          ? rsvp.guestNames.split(/[,\n]+/).map(n => n.trim()).filter(n => n.length > 0)
          : [];
        const kidsCount = parseInt(rsvp.kids) || 0;
        kidsTotal += kidsCount;
        rsvpLookup[fullName] = { kids: kidsCount, guestNames: guestNamesList };
        if (rsvp.guestNames) {
          const names = rsvp.guestNames
            .split(/[,\n]+/)
            .map(n => n.trim())
            .filter(n => n.length > 0);
          allGuests.push(...names);
        }
        for (let i = 1; i <= kidsCount; i++) {
          allGuests.push(`${fullName} child ${i}`);
        }
      });
      setGuests(allGuests);
      setRsvpMap(rsvpLookup);
      setTotalKids(kidsTotal);
      setRsvpList(rsvpData.filter(r => r.attending === 'yes'));
      setTables(seatingData.tables || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Focus search input when opened
  useEffect(() => {
    if (activeSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [activeSearch]);

  // Scroll to and highlight focused table
  useEffect(() => {
    if (focusTableId && !loading) {
      const el = document.getElementById(`table-card-${focusTableId}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        setHighlightedTable(focusTableId);
        setTimeout(() => setHighlightedTable(null), 2000);
      }
      if (onFocusHandled) onFocusHandled();
    }
  }, [focusTableId, loading, onFocusHandled]);

  // Close search on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (activeSearch && !e.target.closest('.seat-search')) {
        setActiveSearch(null);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [activeSearch]);

  // All currently assigned guest names
  const assignedGuests = new Set();
  tables.forEach(t => {
    t.seats.forEach(s => { if (s) assignedGuests.add(s); });
    (t.overflow || []).forEach(s => assignedGuests.add(s));
  });

  const unassignedGuests = guests.filter(g => !assignedGuests.has(g));

  const filteredGuests = searchTerm
    ? unassignedGuests.filter(g => g.toLowerCase().includes(searchTerm.toLowerCase()))
    : unassignedGuests;

  const saveSeating = async (newTables) => {
    setSaving(true);
    await fetch('/api/admin/seating', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tables: newTables }),
    });
    setSaving(false);
  };

  const addTable = (size) => {
    const newTable = {
      id: `table-${Date.now()}`,
      name: `Table ${tables.length + 1}`,
      size,
      seats: Array(size).fill(null),
    };
    const newTables = [newTable, ...tables];
    setTables(newTables);
    saveSeating(newTables);
  };

  const removeTable = (tableId) => {
    const newTables = tables.filter(t => t.id !== tableId);
    setTables(newTables);
    saveSeating(newTables);
  };

  const renameTimeoutRef = useRef(null);
  const renameTable = (tableId, newName) => {
    const newTables = tables.map(t =>
      t.id === tableId ? { ...t, name: newName } : t
    );
    setTables(newTables);
    // Debounce save so we don't fire on every keystroke
    clearTimeout(renameTimeoutRef.current);
    renameTimeoutRef.current = setTimeout(() => saveSeating(newTables), 500);
  };

  const addAndAssignGuest = (tableId, seatIndex, guestName, kids = 0) => {
    const nameParts = guestName.trim().split(/\s+/);
    const firstName = nameParts[0] || guestName.trim();
    const lastName = nameParts.slice(1).join(' ') || '';

    fetch('/api/admin/rsvps', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        firstName,
        lastName,
        attending: 'yes',
        guests: '1',
        guestNames: '',
        kids,
      }),
    });

    setGuests(prev => [...prev, guestName]);
    if (kids > 0) {
      setRsvpMap(prev => ({ ...prev, [guestName]: { kids } }));
    }
    assignSeat(tableId, seatIndex, guestName);
  };

  const assignSeat = (tableId, seatIndex, guestName) => {
    const newTables = tables.map(t => {
      if (t.id !== tableId) return t;
      const newSeats = [...t.seats];
      newSeats[seatIndex] = guestName;

      // Auto-seat guest attendees and kids in available seats at the same table
      const overflow = [...(t.overflow || [])];
      if (guestName && rsvpMap[guestName]) {
        const { guestNames: attendees = [], kids: kidsCount = 0 } = rsvpMap[guestName];
        const toPlace = [
          ...attendees.map(a => `${a} (${guestName})`),
          ...Array.from({ length: kidsCount }, (_, i) => `${guestName} child ${i + 1}`),
        ];
        let idx = 0;
        for (let i = 0; i < newSeats.length && idx < toPlace.length; i++) {
          if (i !== seatIndex && !newSeats[i]) {
            newSeats[i] = toPlace[idx++];
          }
        }
        // Overflow remaining kids (max 2) into center of table
        while (idx < toPlace.length && overflow.length < 2) {
          overflow.push(toPlace[idx++]);
        }
      }

      return { ...t, seats: newSeats, overflow };
    });
    setTables(newTables);
    setActiveSearch(null);
    setSearchTerm('');
    saveSeating(newTables);
  };

  const removeOverflow = (tableId, overflowIndex) => {
    const newTables = tables.map(t => {
      if (t.id !== tableId) return t;
      const overflow = [...(t.overflow || [])];
      overflow.splice(overflowIndex, 1);
      return { ...t, overflow };
    });
    setTables(newTables);
    saveSeating(newTables);
  };

  const clearSeat = (tableId, seatIndex) => {
    assignSeat(tableId, seatIndex, null);
  };

  const openSearch = (tableId, seatIndex) => {
    setActiveSearch({ tableId, seatIndex });
    setSearchTerm('');
  };

  // Find attendees and children associated with a guest
  const getAssociatedNames = (guestName) => {
    if (!guestName) return [];
    return (seatName) => {
      if (!seatName || seatName === guestName) return false;
      // Match "child N" pattern
      if (seatName.startsWith(`${guestName} child `)) return true;
      // Match "AttendeeName (guestName)" pattern
      if (seatName.endsWith(`(${guestName})`)) return true;
      return false;
    };
  };

  // Drag and drop handlers
  const handleDragStart = (e, tableId, seatIndex) => {
    setDragSource({ tableId, seatIndex });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, targetTableId, targetSeatIndex) => {
    e.preventDefault();
    if (!dragSource) return;

    const { tableId: srcTableId, seatIndex: srcSeatIndex } = dragSource;
    if (srcTableId === targetTableId && srcSeatIndex === targetSeatIndex) {
      setDragSource(null);
      return;
    }

    const newTables = tables.map(t => {
      const newSeats = [...t.seats];
      const overflow = [...(t.overflow || [])];
      return { ...t, seats: newSeats, overflow };
    });

    const srcTable = newTables.find(t => t.id === srcTableId);
    const targetTable = newTables.find(t => t.id === targetTableId);
    const srcGuest = srcTable.seats[srcSeatIndex];
    const isAssociated = getAssociatedNames(srcGuest);

    // Collect associated guests and overflow from source table
    const associatedNames = [];
    for (let i = 0; i < srcTable.seats.length; i++) {
      if (i !== srcSeatIndex && isAssociated(srcTable.seats[i])) {
        associatedNames.push(srcTable.seats[i]);
        srcTable.seats[i] = null;
      }
    }
    const remainingOverflow = [];
    for (const name of srcTable.overflow) {
      if (isAssociated(name)) {
        associatedNames.push(name);
      } else {
        remainingOverflow.push(name);
      }
    }
    srcTable.overflow = remainingOverflow;

    // Place main guest at target seat (swap if occupied)
    const targetGuest = targetTable.seats[targetSeatIndex];
    srcTable.seats[srcSeatIndex] = targetGuest;
    targetTable.seats[targetSeatIndex] = srcGuest;

    // Place associated guests in available seats at the target table
    let idx = 0;
    for (let i = 0; i < targetTable.seats.length && idx < associatedNames.length; i++) {
      if (!targetTable.seats[i]) {
        targetTable.seats[i] = associatedNames[idx++];
      }
    }
    // Overflow remaining (max 2 total)
    while (idx < associatedNames.length && targetTable.overflow.length < 2) {
      targetTable.overflow.push(associatedNames[idx++]);
    }

    setTables(newTables);
    setDragSource(null);
    saveSeating(newTables);
  };

  // Table reorder drag handlers
  const handleTableDragStart = (e, tableId) => {
    setDragTable(tableId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleTableDragOver = (e, tableId) => {
    e.preventDefault();
    if (dragTable && dragTable !== tableId) {
      setDragOverTable(tableId);
    }
  };

  const handleTableDrop = (e, targetTableId) => {
    e.preventDefault();
    if (!dragTable || dragTable === targetTableId) {
      setDragTable(null);
      setDragOverTable(null);
      return;
    }
    const srcIdx = tables.findIndex(t => t.id === dragTable);
    const targetIdx = tables.findIndex(t => t.id === targetTableId);
    const newTables = [...tables];
    const [moved] = newTables.splice(srcIdx, 1);
    newTables.splice(targetIdx, 0, moved);
    setTables(newTables);
    setDragTable(null);
    setDragOverTable(null);
    saveSeating(newTables);
  };

  const handleTableDragEnd = () => {
    setDragTable(null);
    setDragOverTable(null);
  };

  if (loading) return <div className="admin-container"><p>Loading...</p></div>;

  return (
    <div className="seating-container">
      <header style={{ textAlign: 'center', padding: '20px' }}>
        <h1>Seating Plan</h1>
        <div className="seating-stats">
          <span className="total-badge">
            Seated: <strong>{assignedGuests.size}</strong> / {guests.length} (Adults: {guests.length - totalKids} | Kids: {totalKids})
          </span>
          {saving && <span className="saving-indicator">Saving...</span>}
        </div>
      </header>

      <div className="seating-controls">
        <button onClick={() => addTable(6)}>+ Table of 6</button>
        <button onClick={() => addTable(10)}>+ Table of 10</button>
      </div>

      <div className="tables-list-grid">
        {tables.map(table => (
          <div
            key={table.id}
            id={`table-card-${table.id}`}
            className={`table-card${dragTable === table.id ? ' table-dragging' : ''}${dragOverTable === table.id ? ' table-drag-over' : ''}${highlightedTable === table.id ? ' table-card-highlight' : ''}`}
            onDragOver={(e) => { if (dragTable) handleTableDragOver(e, table.id); }}
            onDrop={(e) => { if (dragTable) handleTableDrop(e, table.id); }}
            onDragEnd={handleTableDragEnd}
          >
            <div className="table-card-header">
              <div
                className="table-drag-handle"
                draggable
                onDragStart={(e) => handleTableDragStart(e, table.id)}
                title="Drag to reorder"
              >
                ⠿
              </div>
              <input
                className="table-name-input"
                value={table.name}
                onChange={(e) => renameTable(table.id, e.target.value)}
              />
              <span className="table-card-count">
                {table.seats.filter(Boolean).length}/{table.size}
              </span>
              <button className="remove-table-btn" onClick={() => removeTable(table.id)}>
                &times;
              </button>
            </div>

            <ul className="table-card-seats">
              {table.seats.map((guest, seatIdx) => {
                const isOccupied = !!guest;
                const isSearchOpen = activeSearch?.tableId === table.id && activeSearch?.seatIndex === seatIdx;

                return (
                  <li
                    key={seatIdx}
                    className={`seat-row ${isOccupied ? 'occupied' : 'empty'}`}
                    draggable={isOccupied}
                    onDragStart={isOccupied ? (e) => handleDragStart(e, table.id, seatIdx) : undefined}
                    onDragOver={handleDragOver}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (!dragSource) return;
                      if (dragSource.guest) {
                        assignSeat(table.id, seatIdx, dragSource.guest);
                        setDragSource(null);
                      } else {
                        handleDrop(e, table.id, seatIdx);
                      }
                    }}
                    onClick={!isOccupied && !isSearchOpen ? () => openSearch(table.id, seatIdx) : undefined}
                  >
                    <span className="seat-row-number">{seatIdx + 1}</span>
                    {isOccupied ? (
                      <>
                        <span className="seat-row-name">{guest}</span>
                        <button
                          className="seat-row-remove"
                          onClick={(e) => { e.stopPropagation(); clearSeat(table.id, seatIdx); }}
                        >
                          &times;
                        </button>
                      </>
                    ) : (
                      <span className="seat-row-empty">+ Add guest</span>
                    )}

                    {isSearchOpen && (
                      <div className="seat-search seat-search-list" onClick={e => e.stopPropagation()}>
                        <input
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search guest..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <div className="search-results">
                          {filteredGuests.slice(0, 8).map((g, i) => (
                            <div
                              key={i}
                              className="search-result-item"
                              onClick={() => assignSeat(table.id, seatIdx, g)}
                            >
                              {g}
                            </div>
                          ))}
                          {searchTerm.trim() && !guests.some(g => g.toLowerCase() === searchTerm.trim().toLowerCase()) && (
                            <div
                              className="search-result-item search-add-new"
                              onClick={() => addAndAssignGuest(table.id, seatIdx, searchTerm.trim())}
                            >
                              + Add "{searchTerm.trim()}"
                            </div>
                          )}
                          {filteredGuests.length === 0 && !searchTerm.trim() && (
                            <div className="search-no-results">No guests found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {(table.overflow || []).length > 0 && (
              <div className="table-card-overflow">
                <span className="overflow-label">Overflow:</span>
                {table.overflow.map((kid, i) => (
                  <span key={i} className="overflow-chip">
                    {kid}
                    <button
                      className="overflow-remove"
                      onClick={() => removeOverflow(table.id, i)}
                    >&times;</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Unassigned guests list */}
      {unassignedGuests.length > 0 && (
        <div className="unassigned-panel">
          <h3>Unassigned ({unassignedGuests.length})</h3>
          <div className="unassigned-list">
            {unassignedGuests.map((guest, i) => (
              <span
                key={i}
                className="unassigned-chip"
                draggable
                onDragStart={(e) => {
                  setDragSource({ guest });
                  e.dataTransfer.effectAllowed = 'move';
                }}
              >
                {guest}
              </span>
            ))}
          </div>
        </div>
      )}
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <button className="print-labels-btn" onClick={() => setShowLabels(true)}>
          Print Labels
        </button>
      </div>

      {showLabels && (
        <div className="labels-overlay">
          <div className="labels-toolbar">
            <button onClick={() => { window.print(); }}>Print</button>
            <button onClick={() => setShowLabels(false)}>Close</button>
          </div>
          <div className="labels-pages">
            {rsvpList.map((rsvp, i) => {
              const name = `${rsvp.firstName} ${rsvp.lastName}`.trim();
              const guestNames = rsvp.guestNames
                ? rsvp.guestNames.split(/[,\n]+/).map(n => n.trim()).filter(n => n.length > 0)
                : [];
              const kids = parseInt(rsvp.kids) || 0;
              const kidsText = kids > 0
                ? (kids === 1 ? '+1 Παιδί' : `+${kids} Παιδιά`)
                : '';

              const labelContent = (
                <>
                  <div className="label-name">{name}</div>
                  {guestNames.length > 0 && (
                    <div className="label-guests">
                      {guestNames.join(', ')}
                    </div>
                  )}
                  {kidsText && <div className="label-kids">{kidsText}</div>}
                </>
              );

              return (
                <div key={i} className="label-page">
                  <div className="label-half label-top">
                    {labelContent}
                  </div>
                  <div className="label-fold-line" />
                  <div className="label-half label-bottom">
                    {labelContent}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SeatingPlan;
